import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import { catchError, concat, filter, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { AnalyticsEvent, AnalyticsService } from '../../analytics';
import { CourseActionsPayload, LectureV3, PartialEntity, SectionPosition, StreamVideoInfoV3 } from '../../models';
import {
  AnnotationService,
  CourseService,
  CoursesService,
  InstructorService,
  LoaderService,
  PaginationService,
  ToastService,
} from '../../services';
import { LiveSessionService } from '../../services/live-session/live-session.service';
import { isDefined, UtilsService } from '../../utils';
import { CourseActions } from '../course';
import { CourseActionsV2 } from './course.actions';
import { selectCourseCurriculumV2, selectCourseV2 } from './course.selectors';

/**
 * Round-trip an unknown value through JSON to strip non-serializable
 * members (functions, undefined, symbols) and surface circular refs.
 * Primitives pass through untouched; objects/arrays become plain data.
 * Used when forwarding HTTP error bodies into NgRx actions so the store's
 * `strictActionSerializability` runtime check doesn't fail on
 * ProgressEvent / ErrorEvent payloads.
 */
function safeSerializeBody(value: unknown): unknown {
  if (value == null) return null;
  if (typeof value !== 'object') return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    // Unserializable (circular refs or exotic types) — surface the type
    // name so consumers have at least a breadcrumb.
    return { _unserializable: Object.prototype.toString.call(value) };
  }
}

@Injectable()
export class CourseDetailsV2Effects {
  private readonly actions$ = inject(Actions);
  private readonly coursesService = inject(CourseService);
  private readonly loaderService = inject(LoaderService);
  private readonly instructorService = inject(InstructorService);
  private readonly store = inject(Store);
  private readonly annotationService = inject(AnnotationService);
  private readonly utilsService = inject(UtilsService);
  private readonly analytics = inject(AnalyticsService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly coursesServiceV2 = inject(CoursesService);
  private readonly liveSessionService = inject(LiveSessionService);
  private paginationService = inject(PaginationService);

  // Instructor
  createCourse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.createCourse),
      switchMap(({ title, categoryId, userId, language, course_mode }) => {
        this.loaderService.loaderAction('course-creating', 'processing');
        return this.instructorService
          .createCourseV2({ title, category: categoryId, user: userId, language: language ?? 'ar', course_mode })
          .pipe(
            tap((course) => this.router.navigate(['/instructor/course', course.id, 'intended-learners'])),
            map((course) => {
              this.loaderService.loaderAction('course-creating', 'completed');
              // Data layer
              this.analytics.track(AnalyticsEvent.COURSE_CREATED, {
                course_id: course.id,
                course_name: course.title,
                user_id: userId,
                user_name: course.user?.fullname ?? '',
              });
              return CourseActionsV2.createCourseSuccess({ course: course });
            }),
            catchError(({ error }) => {
              this.loaderService.loaderAction('course-creating', 'completed');
              const message =
                (typeof error?.error === 'string' && error.error) ||
                error?.errors?.[0]?.detail;
              if (message) {
                this.toastService.error({ message });
              }
              return of(CourseActionsV2.createCourseError());
            })
          );
      })
    )
  );

  // Load instructor course info
  loadInstructorCourseInfoV2 = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadInstructorCourseInfoV2),
      mergeMap(({ courseId, params }) => {
        const actionsToDispatch = [
          CourseActionsV2.loadCourseLearner({ courseId, params: { mode: params?.mode } }),
          CourseActionsV2.loadCourseDetails({ courseId, params: { mode: params?.mode } }),
          CourseActionsV2.loadCourseCurriculum({
            courseId,
            params: { page: 1, page_size: params?.page_size, show_is_deleted: false, mode: params?.mode },
          }),
        ];

        return actionsToDispatch;
      })
    )
  );

  loadInstructorCourseDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadCourseDetails),
      switchMap(({ courseId, params }) => {
        this.loaderService.loaderAction('loading-instructor-courses-detailsV2', 'loading');
        return this.coursesService.getInstructorCourseDetails(courseId, params).pipe(
          map((data) => {
            this.loaderService.loaderAction('loading-instructor-courses-detailsV2', 'success');
            return CourseActionsV2.loadCourseDetailsSuccess(data);
          }),
          catchError((error) => {
            this.loaderService.loaderAction('loading-instructor-courses-detailsV2', 'failed');
            // Serialize error to avoid NgRx strictActionSerializability issues
            const serializedError = {
              status: error?.status,
              statusText: error?.statusText,
              message: error?.message,
              error: error?.error,
            };
            return of(CourseActionsV2.loadCourseDetailsFail({ error: serializedError }));
          })
        );
      })
    )
  );

  loadCourseSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadCourseDetailsSuccess),
      concatLatestFrom(() => this.store.select(selectCourseV2)),
      mergeMap(([, courseDetail]) => {
        const actionsToDispatch = [
          CourseActions.loadHistory({ projectId: courseDetail.project }),
          CourseActionsV2.loadCoursePermissions({ project_id: courseDetail.project }),
        ];
        const actionsToDispatchForClassSetup = [
          CourseActionsV2.loadClassSetup({ projectId: courseDetail.project, params: {} }),
        ];
        return courseDetail.course_mode === 'video_based'
          ? actionsToDispatch
          : [...actionsToDispatchForClassSetup, ...actionsToDispatch];
      })
    )
  );

  upsertCourse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.upsertCourse),
      switchMap(({ courseDetails }) => {
        this.loaderService.loaderAction('prevent-button-action-based-on-api-response', 'loading');
        this.loaderService.loaderAction('course-auto-saving', 'loading');
        // courseDetails = omit(courseDetails, ['promo_video',  'video_info']);

        let body = {
          title: courseDetails.title,
          subtitle: courseDetails.subtitle,
          desc: courseDetails.desc,
          image: courseDetails.image,
          skill_level: courseDetails.skill_level,
          category: courseDetails.category,
          subcategory: courseDetails.subcategory,
          language: courseDetails.language,
          topics: courseDetails.topics,
          primary_topic: courseDetails.primary_topic ?? null,
        };
        return this.coursesService.updateCourse(courseDetails.id, body).pipe(
          map((courseDetails) => {
            this.loaderService.loaderAction('prevent-button-action-based-on-api-response', 'success');
            this.loaderService.loaderAction('course-auto-saving', 'success');
            this.loaderService.loaderAction('course-save-action', 'success');
            return CourseActionsV2.upsertCourseSuccess({ courseDetails });
          }),
          catchError((error) => {
            this.loaderService.loaderAction('course-auto-saving', 'failed');
            this.loaderService.loaderAction('course-save-action', 'failed');
            this.loaderService.loaderAction('prevent-button-action-based-on-api-response', 'failed');
            return of(CourseActionsV2.upsertCourseError({ error }));
          })
        );
      })
    )
  );

  loadInstructorCourseLearner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadCourseLearner),
      switchMap(({ courseId, params }) => {
        setTimeout(() => {
          this.loaderService.loaderAction('loading-instructor-courses-learnerV2', 'loading');
        });
        return this.coursesService.getInstructorCourseLearner(courseId, params).pipe(
          map((data) => {
            this.loaderService.loaderAction('loading-instructor-courses-learnerV2', 'success');
            return CourseActionsV2.loadCourseLearnerSuccess(data);
          }),
          catchError((error) => {
            const serializedError = {
              status: error?.status,
              statusText: error?.statusText,
              message: error?.message,
              error: error?.error,
            };
            return of(CourseActionsV2.loadCourseLearnerFail({ error: serializedError }));
          })
        );
      })
    )
  );

  loadInstructorCourseCurriculum$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadCourseCurriculum),
      switchMap(({ courseId, params }) => {
        setTimeout(() => {
          this.loaderService.loaderAction('loading-instructor-courses-curriculumV2', 'loading');
        });
        return this.coursesService.getInstructorCourseCurriculum(courseId, params).pipe(
          map((data) => {
            this.loaderService.loaderAction('loading-instructor-courses-curriculumV2', 'success');
            return CourseActionsV2.loadCourseCurriculumSuccess(data);
          }),
          catchError((error) => {
            this.loaderService.loaderAction('loading-instructor-courses-curriculumV2', 'failed');
            const serializedError = {
              status: error?.status,
              statusText: error?.statusText,
              message: error?.message,
              error: error?.error,
            };
            return of(CourseActionsV2.loadCourseCurriculumFail({ error: serializedError }));
          })
        );
      })
    )
  );

  // Section
  createSectionV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.createSectionV2),
      filter(isDefined),
      switchMap(({ section }) => {
        let payload = {
          id: section.id,
          course: section.course,
          title: section.title,
          position: section.position,
        };
        return this.coursesService.createSectionV3(payload).pipe(
          map(() => CourseActionsV2.upsertSectionSuccessV2({ courseId: section.course })),
          catchError(() => of(CourseActionsV2.upsertSectionErrorV2()))
        );
      })
    )
  );

  upsertSectionV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.upsertSectionV2),
      filter(isDefined),
      withLatestFrom(this.store.select(selectCourseV2)),
      switchMap(([{ section }, courseDetails]) => {
        const courseId = section.course || courseDetails?.id || '';
        let payload = {
          id: section.id,
          title: section.title,
        };
        return this.coursesService.updateSectionV3(payload, courseId).pipe(
          map(() => CourseActionsV2.upsertSectionSuccessV2({ courseId })),
          catchError(() => of(CourseActionsV2.upsertSectionErrorV2()))
        );
      })
    )
  );

  upsertSectionPositionV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.upsertSectionSuccessV2),
      withLatestFrom(this.store.select(selectCourseCurriculumV2)),
      mergeMap(([{ courseId }, curriculum]) => {
        let payload: SectionPosition[] = [];
        curriculum.results.sections.forEach((data, index) => {
          payload.push({ position: index, section: data.id });
        });
        if (courseId) {
          return this.instructorService.upsertSectionPosition(payload, courseId).pipe(
            map((res) => CourseActionsV2.upsertSectionPositionSuccessV2({ result: res })),
            catchError((error) => of(CourseActionsV2.upsertSectionPositionErrorV2()))
          );
        } else {
          return of(CourseActionsV2.upsertSectionPositionErrorV2());
        }
      })
    )
  );

  deleteSectionV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.deleteSectionV2),
      switchMap(({ sectionId, params }) => {
        this.loaderService.loaderAction('deleting-instructor-course-section', 'loading');
        return this.coursesService.deleteSectionV3(sectionId, params).pipe(
          map(() => {
            this.loaderService.loaderAction('deleting-instructor-course-section', 'success');
            return CourseActionsV2.deleteSectionSuccessV2({ sectionId });
          }),
          catchError(({ error }) => {
            this.toastService.info({
              message: $localize`This section cannot be deleted because it contains subsections. Please move or delete the subsections and try again.`,
            });
            this.loaderService.loaderAction('deleting-instructor-course-section', 'failed');
            return of(CourseActionsV2.deleteSectionErrorV2());
          })
        );
      })
    )
  );

  deleteSubsection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.deleteSubsectionV2),
      switchMap(({ subsectionId }) =>
        this.coursesService.deleteSubsectionV3(subsectionId).pipe(
          map(() => CourseActionsV2.deleteSubsectionSuccessV2()),
          catchError(() => of(CourseActionsV2.deleteSubsectionErrorV2()))
        )
      )
    )
  );

  softDeleteSubsection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.softDeleteSubsectionToggleV2),
      switchMap(({ subsectionId, isDelete, activityId, courseId }) => {
        if (isDelete) {
          return this.coursesService.deleteSubsectionV3(subsectionId).pipe(
            map(() => CourseActionsV2.deleteSubsectionSuccessV2()),
            catchError(() => of(CourseActionsV2.deleteSubsectionErrorV2()))
          );
        } else {
          return this.coursesService.restoreSubsectionV3(courseId!, subsectionId!).pipe(
            map(() => CourseActionsV2.deleteSubsectionSuccessV2()),
            catchError(() => of(CourseActionsV2.deleteSubsectionErrorV2()))
          );
        }
      })
    )
  );

  // Courses lecture
  createLectureV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.createLectureV2),
      switchMap(({ subsection, lecture }) => {
        // Single API call - backend creates both lecture AND subsection together
        // This ensures SSE event contains all data needed for real-time updates
        const subsectionData = {
          section: subsection!.section!,
          position: subsection!.position!
        };

        return this.coursesService.upsertLectureV3(lecture!, subsectionData).pipe(
          map((response) => {
            // Backend returns lecture with subsection data included
            const createdSubsection = response.subsection;
            return CourseActionsV2.createLectureSuccessV2({
              subsection: createdSubsection ? {
                ...subsection,
                id: createdSubsection.id
              } : subsection,
              lecture: response
            });
          }),
          catchError(() => of(CourseActionsV2.createLectureErrorV2()))
        );
      })
    )
  );

  upsertLectureV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.upsertLectureV2),
      switchMap(({ lecture }) => {
        this.loaderService.loaderAction('update-courses-lecture', 'loading');
        return concat(
          this.coursesService.upsertLectureV3(omitBy(lecture!, isNil) as PartialEntity<LectureV3>).pipe(
            map((lecture) => {
              this.loaderService.loaderAction('update-courses-lecture', 'success');
              return CourseActionsV2.upsertLectureSuccessV2({ lecture });
            }),
            catchError(() => {
              this.toastService.message({
                message: $localize`Lecture save failed.`,
              });
              this.loaderService.loaderAction('update-courses-lecture', 'failed');
              return of(CourseActionsV2.upsertLectureErrorV2());
            })
          )
        );
      })
    )
  );

  upsertLectureResourceV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.upsertLectureResourceV2),
      switchMap(({ lecture, lectureResources }) =>
        concat(
          // ...lectureResources.map((lectureResource) =>
          this.coursesService.upsertLectureResourceV3(lectureResources, lecture.id).pipe(
            map((resource) =>
              CourseActionsV2.upsertLectureResourceSuccessV2({ lecture, updatedLectureResources: resource })
            ),
            catchError(() => of(CourseActionsV2.upsertLectureResourceErrorV2()))
          )
          // )
        )
      )
    )
  );

  upsertLectureSuccessV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.upsertLectureSuccessV2),
      mergeMap(({ lecture }) => {
        if (lecture.type === 'video' && lecture.video_info?.task_token) {
          return of(
            CourseActionsV2.selectLectureVideoInfoFromLibraryV2({
              video_info: lecture.video_info as StreamVideoInfoV3,
              lecture_id: lecture.id,
            })
          );
        } else {
          return of();
        }
      })
    )
  );

  deleteLectureResourceV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.deleteLectureResourceV2),
      switchMap(({ lectureResourceId, lectureId }) =>
        this.instructorService.deleteLectureResource(lectureId, lectureResourceId).pipe(
          map(() => CourseActionsV2.deleteLectureResourceSuccessV2()),
          catchError(() => of(CourseActionsV2.deleteLectureResourceErrorV2()))
        )
      )
    )
  );

  // Quiz - Single API call that creates quiz + subsection together
  createQuizV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.createQuizV2),
      switchMap(({ subsection, quiz }) => {
        // Use single API call - backend creates subsection automatically
        const subsectionData = {
          section: subsection!.section!,
          position: subsection!.position!
        };
        return this.coursesService.upsertQuizV3(quiz!, subsectionData).pipe(
          map((response) => {
            // Get the subsection from response
            const createdSubsection = response.subsection;
            return CourseActionsV2.createQuizSuccessV2({
              subsection: createdSubsection ? { ...subsection, id: createdSubsection.id } : subsection,
              quiz: response
            });
          }),
          catchError(() => of(CourseActionsV2.createQuizErrorV2()))
        );
      })
    )
  );

  upsertQuizV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.upsertQuizV2),
      switchMap((quiz) => {
        this.loaderService.loaderAction('update-courses-quize', 'loading');
        return this.coursesService.upsertQuizV3(quiz.quiz).pipe(
          map(() => {
            this.loaderService.loaderAction('update-courses-quize', 'success');
            return CourseActionsV2.upsertQuizSuccessV2();
          }),
          catchError(() => {
            this.toastService.error({
              message: $localize`Quiz save failed.`,
            });
            this.loaderService.loaderAction('update-courses-quize', 'failed');
            return of(CourseActionsV2.upsertQuizErrorV2());
          })
        );
      })
    )
  );

  createQuizQuestionV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.createQuizQuestionV2),
      switchMap(({ quizQuestion, quizAnswers }) =>
        concat(
          // need to remove null values, i.e. lecture since it's not required
          this.coursesService.upsertQuizQuestionV3(omitBy(quizQuestion!, isNil) as any)
          // ...quizAnswers?.map((quizAnswer) => this.coursesService.upsertQuizAnswerV3(quizAnswer!))
        ).pipe(
          map(() => CourseActionsV2.upsertQuizQuestionSuccessV2()),
          catchError(() => of(CourseActionsV2.upsertQuizQuestionErrorV2()))
        )
      )
    )
  );

  upsertQuizQuestionV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.upsertQuizQuestionV2),
      switchMap(({ quizQuestion, quizAnswers }) => {
        this.loaderService.loaderAction('update-courses-quize', 'loading');
        return concat(
          // need to remove null values, i.e. lecture since it's not required
          this.coursesService.upsertQuizQuestionSettingsV3(omitBy(quizQuestion!, isNil) as any)
          // this.coursesService.upsertQuizQuestionV3(omitBy(quizQuestion!, isNil) as any),
          // ...quizAnswers?.map((quizAnswer) => this.coursesService.upsertQuizAnswerV3(quizAnswer!))
        ).pipe(
          map(() => {
            this.loaderService.loaderAction('update-courses-quize', 'success');
            return CourseActionsV2.upsertQuizQuestionSuccessV2();
          }),
          catchError(() => {
            this.toastService.error({
              message: $localize`Quiz question save failed.`,
            });
            this.loaderService.loaderAction('update-courses-quize', 'failed');
            return of(CourseActionsV2.upsertQuizQuestionErrorV2());
          })
        );
      })
    )
  );
  upsertQuizSettingsV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.upsertQuizSettingsV2),
      switchMap(({ quizQuestion }) =>
        concat(this.coursesService.upsertQuizQuestionSettingsV3(omitBy(quizQuestion!, isNil) as any)).pipe(
          map(() => CourseActionsV2.upsertQuizQuestionSuccessV2()),
          catchError(() => of(CourseActionsV2.upsertQuizQuestionErrorV2()))
        )
      )
    )
  );
  deleteQuizQuestionV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.deleteQuizQuestionV2),
      switchMap(({ quizQuestionId }) =>
        this.coursesService.deleteQuizQuestionV3(quizQuestionId).pipe(
          map(() => CourseActionsV2.deleteQuizQuestionSuccessV2()),
          catchError(() => of(CourseActionsV2.deleteQuizQuestionErrorV2()))
        )
      )
    )
  );

  deleteQuizAnswerV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.deleteQuizAnswerV2),
      switchMap(({ quizAnswerId }) =>
        this.coursesService.deleteQuizAnswerV3(quizAnswerId).pipe(
          map(() => CourseActionsV2.deleteQuizAnswerSuccessV2()),
          catchError(() => of(CourseActionsV2.deleteQuizAnswerErrorV2()))
        )
      )
    )
  );

  // Assignments
  createAssignmentV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.createAssignmentV2),
      switchMap(({ subsection, assignment }) =>
        concat(
          this.coursesService.upsertSubsectionV3(omit(subsection!, 'assignment')),
          this.coursesService.upsertAssignmentV3(assignment!),
          this.coursesService.upsertSubsectionV3({ id: subsection!.id, assignment: assignment!.id })
        ).pipe(
          map(() => CourseActionsV2.createAssignmentSuccessV2()),
          catchError(() => of(CourseActionsV2.createAssignmentErrorV2()))
        )
      )
    )
  );

  upsertAssignmentV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.upsertAssignmentV2),
      switchMap(({ assignment, assignmentQuestions }) => {
        this.loaderService.loaderAction('update-courses-assignment', 'loading');
        return concat(
          // need to remove null values, i.e. lecture since it's not required
          this.coursesService.upsertAssignmentV3(omitBy(assignment!, isNil) as any),
          ...assignmentQuestions?.map((assignmentQuestion) =>
            this.coursesService.upsertAssignmentQuestionV3(assignmentQuestion!)
          )
        ).pipe(
          map(() => {
            this.loaderService.loaderAction('update-courses-assignment', 'success');
            return CourseActionsV2.upsertAssignmentSuccessV2();
          }),
          catchError(() => {
            this.toastService.error({
              message: $localize`Failed to save assignment. Please try again.`,
            });
            this.loaderService.loaderAction('update-courses-assignment', 'failed');
            return of(CourseActionsV2.upsertAssignmentErrorV2());
          })
        );
      })
    )
  );

  deleteAssignmentQuestionV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.deleteAssignmentQuestionV2),
      switchMap(({ assignmentQuestionId }) =>
        this.instructorService.deleteAssignmentQuestion(assignmentQuestionId).pipe(
          map(() => CourseActionsV2.deleteAssignmentQuestionSuccessV2()),
          catchError(() => of(CourseActionsV2.deleteAssignmentQuestionErrorV2()))
        )
      )
    )
  );

  // Unified assignment with questions
  upsertAssignmentWithQuestionsV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.upsertAssignmentWithQuestionsV2),
      switchMap(({ assignment, subsection }) => {
        this.loaderService.loaderAction('update-courses-assignment', 'loading');

        // Determine if this is a create or update operation
        // CREATE: subsection has position field (new subsection being added)
        // UPDATE: subsection only has id field (existing assignment being updated)
        const isCreating = subsection && subsection.position !== undefined;

        if (isCreating) {
          // CREATE flow: Backend creates both assignment and subsection together
          // This ensures the SSE event contains all data needed for real-time updates
          const subsectionData = {
            section: subsection!.section!,
            position: subsection!.position!
          };

          return this.coursesService.upsertAssignmentWithQuestions(
            assignment,
            undefined, // No ID = create
            subsectionData
          ).pipe(
            map((response) => {
              this.loaderService.loaderAction('update-courses-assignment', 'success');
              // Backend returns assignment with subsection data included
              const savedAssignment = response;
              const createdSubsection = response.subsection;

              return CourseActionsV2.upsertAssignmentWithQuestionsSuccessV2({
                assignment: savedAssignment,
                subsection: createdSubsection ? {
                  ...subsection,
                  id: createdSubsection.id
                } : subsection
              });
            }),
            catchError(() => {
              this.toastService.error({
                message: $localize`Failed to create assignment. Please try again.`,
              });
              this.loaderService.loaderAction('update-courses-assignment', 'failed');
              return of(CourseActionsV2.upsertAssignmentWithQuestionsErrorV2());
            })
          );
        } else {
          // UPDATE flow: Just update the assignment
          return this.coursesService.upsertAssignmentWithQuestions(
            assignment,
            assignment.id // Pass ID for PATCH
          ).pipe(
            map((savedAssignment) => {
              this.loaderService.loaderAction('update-courses-assignment', 'success');
              return CourseActionsV2.upsertAssignmentWithQuestionsSuccessV2({
                assignment: savedAssignment,
                subsection
              });
            }),
            catchError(() => {
              this.toastService.error({
                message: $localize`Failed to save assignment. Please try again.`,
              });
              this.loaderService.loaderAction('update-courses-assignment', 'failed');
              return of(CourseActionsV2.upsertAssignmentWithQuestionsErrorV2());
            })
          );
        }
      })
    )
  );

  deleteAssignmentV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.deleteAssignmentV2),
      switchMap(({ assignmentId }) =>
        this.coursesService.deleteAssignment(assignmentId).pipe(
          map(() => CourseActionsV2.deleteAssignmentSuccessV2()),
          catchError(() => of(CourseActionsV2.deleteAssignmentErrorV2()))
        )
      )
    )
  );

  /**
   * Smart caching: Fetch assignment data when drawer opens
   * Only fetches if assignment not in store or has no questions
   */
  fetchAssignmentOnEdit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.editActivityV2),
      filter(({ activityState }) => activityState === 'type_assignment'),
      concatLatestFrom(({ activityId, sectionId, subsectionId }) =>
        this.store.select(selectCourseCurriculumV2).pipe(
          map((curriculum) => {
            // Find assignment in store
            const section = curriculum.results.sections.find(s => s.id === sectionId);
            const subsection = section?.subsections.find(ss => ss.id === subsectionId);
            const assignment = subsection?.assignment;

            return {
              assignment,
              activityId,
              sectionId,
              subsectionId,
              // Only fetch if assignment EXISTS but questions property is MISSING (undefined)
              // If assignment doesn't exist → being created, don't fetch (404)
              // If assignment has questions array (even empty) → already have full data, don't fetch
              needsFetch: !!assignment && assignment.questions === undefined
            };
          })
        )
      ),
      filter(([_, data]) => data.needsFetch),
      switchMap(([_, { activityId, sectionId, subsectionId }]) =>
        this.coursesService.getAssignmentWithQuestions(activityId).pipe(
          map((assignment) =>
            CourseActionsV2.loadAssignmentSuccessV2({
              assignment,
              sectionId,
              subsectionId
            })
          ),
          catchError(() => of(CourseActionsV2.loadAssignmentErrorV2()))
        )
      )
    )
  );

  /**
   * Fetch quiz data when drawer opens, unless already loaded (e.g., after create).
   * Skips GET if quiz already has `questions` property (even if empty array).
   */
  fetchQuizOnEdit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.editActivityV2),
      filter(({ activityState }) => activityState === 'type_quiz'),
      concatLatestFrom(({ sectionId, subsectionId }) =>
        this.store.select(selectCourseCurriculumV2).pipe(
          map((curriculum) => {
            const section = curriculum.results.sections.find(s => s.id === sectionId);
            const subsection = section?.subsections.find(ss => ss.id === subsectionId);
            const quiz = subsection?.quiz;

            return {
              quiz,
              // Only fetch if quiz EXISTS but questions property is MISSING (undefined)
              // If quiz doesn't exist → being created, don't fetch (404)
              // If quiz has questions array (even empty) → already have full data, don't fetch
              needsFetch: !!quiz && quiz.questions === undefined
            };
          })
        )
      ),
      filter(([_, data]) => data.needsFetch),
      switchMap(([{ activityId, sectionId, subsectionId }]) =>
        this.coursesService.getQuizWithQuestions(activityId).pipe(
          map((quiz) =>
            CourseActionsV2.loadQuizSuccessV2({
              quiz,
              sectionId,
              subsectionId
            })
          ),
          catchError(() => of(CourseActionsV2.loadQuizErrorV2()))
        )
      )
    )
  );

  /**
   * Fetch lecture data when drawer opens if full data not yet loaded.
   *
   * Curriculum API returns outline data only (title, type, video_info, etc.)
   * Lecture GET API returns full data (+ description, article, resources, etc.)
   *
   * Fetching strategy:
   * - description === undefined → never loaded full data → fetch from GET API
   * - description exists (even '') → already loaded → use store data
   *
   * After first fetch, store is maintained via SSE updates.
   * This matches the quiz pattern - fetch once, then rely on store.
   */
  fetchLectureOnEdit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.editActivityV2),
      filter(({ activityState }) => activityState === 'lecture_article' || activityState === 'lecture_video'),
      concatLatestFrom(({ sectionId, subsectionId }) =>
        this.store.select(selectCourseCurriculumV2).pipe(
          map((curriculum) => {
            const section = curriculum.results.sections.find(s => s.id === sectionId);
            const subsection = section?.subsections.find(ss => ss.id === subsectionId);
            const lecture = subsection?.lecture;

            // Only fetch if full data not yet loaded (description is undefined from curriculum API)
            // Once fetched, store is maintained via SSE updates.
            //
            // CRITICAL: also require `chain_id` to be set. `chain_id` is
            // assigned by the backend on lecture creation; locally-created
            // lectures (optimistically added by `createLectureV2` reducer
            // before the BE round-trip completes) have NO `chain_id`. Firing
            // GET /api/v2/courses/lectures/<id>/ for those returns 404
            // because the lecture doesn't exist server-side yet. After the
            // BE creates the lecture, `createLectureSuccessV2` populates
            // `chain_id` + `description` in the store from the BE response —
            // subsequent clicks on the same lecture correctly skip the
            // fetch (description already set). New lectures get their
            // content via the create response + SSE updates, never via
            // this drawer-open fetch path.
            return {
              lecture,
              needsFetch: !!lecture && !!lecture.chain_id && lecture.description === undefined
            };
          })
        )
      ),
      filter(([_, data]) => data.needsFetch),
      switchMap(([{ activityId, sectionId, subsectionId }]) =>
        this.coursesService.getLecture(activityId).pipe(
          map((lecture) =>
            CourseActionsV2.loadLectureSuccessV2({
              lecture,
              sectionId,
              subsectionId
            })
          ),
          catchError(() => of(CourseActionsV2.loadLectureErrorV2()))
        )
      )
    )
  );

  // Annotations
  loadCoursesAnnotationsV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadAnnotationsV2),
      switchMap((params) => {
        return this.coursesService.getAnnotationsByProjectIdV3(params.projectId).pipe(
          map((data) => {
            return CourseActionsV2.loadAnnotationsSuccessV2(data);
          }),
          catchError((error) => {
            const serializedError = {
              status: error?.status,
              statusText: error?.statusText,
              message: error?.message,
              error: error?.error,
            };
            return of(CourseActionsV2.loadCourseDetailsFail({ error: serializedError }));
          })
        );
      })
    )
  );

  loadHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActions.loadHistory),
      switchMap(({ projectId }) =>
        this.annotationService.getAnnotationsByProjectId(projectId).pipe(
          map(({ courses, annotations }) => CourseActions.loadHistorySuccess({ courses, annotations })),
          catchError(() => of(CourseActions.loadHistoryError()))
        )
      )
    )
  );

  upsertLectureVideoInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.upsertLectureVideoInfoV2),
      mergeMap((action) => {
        const actionsToDispatch = [
          CourseActionsV2.uploadStatusUpdateV2({
            referenceId: action.video_info.lecture_id || '',
            status: action.video_info.status.length ? action.video_info.status : 'processing',
            error: action.video_info.error?.length ? action.video_info.error : '',
          }),
          // Dispatch other actions based on the video_info payload
        ];

        return actionsToDispatch;
      })
    )
  );

  // Instructor course permissions
  loadCoursePermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadCoursePermissions),
      switchMap(({ project_id }) => {
        return this.coursesServiceV2.getInstructorPermission({ project_id: project_id }).pipe(
          map((data) => {
            return CourseActionsV2.loadCoursePermissionsSuccess({ coursePermissions: data });
          }),
          catchError((error) => {
            const serializedError = {
              status: error?.status,
              statusText: error?.statusText,
              message: error?.message,
              error: error?.error,
            };
            return of(CourseActionsV2.loadCoursePermissionsFail({ error: serializedError }));
          })
        );
      })
    )
  );

  // Load admin course version history
  loadCourseV2 = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadCourseV2),
      mergeMap((action) => {
        this.loaderService.loaderAction('loading-admin-courses-curriculumV2', 'loading');
        this.loaderService.loaderAction('loading-admin-courses-detailsV2', 'loading');
        this.loaderService.loaderAction('loading-admin-courses-learnerV2', 'loading');
        // TODO: Remove this payload, Once new API will be ready
        let courseActionsPayload: CourseActionsPayload = {
          courseId: '',
        };
        if (action.params?.fixed_source) {
          courseActionsPayload = {
            courseId: action.courseId,
            params: { fixed_source: true },
          };
        } else {
          courseActionsPayload = {
            courseId: action.courseId,
          };
        }
        const actionsToDispatch = [
          // CourseActions.loadCourse(courseActionsPayload),
          CourseActionsV2.loadAdminCourseLearner({ courseId: action.courseId }),
          CourseActionsV2.loadAdminCourseDetails({ courseId: action.courseId }),
          CourseActionsV2.loadAdminCourseCurriculum({ courseId: action.courseId, params: {} }),
          // CourseActions.loadCourse({ courseId: action.courseId, params: { fixed_source: true } })
        ];

        return actionsToDispatch;
      })
    )
  );

  loadCoursesVersionHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadAdminCourseHistory),
      switchMap((params) => {
        return this.coursesService.getCoursesHistoryByProjectId(params.courseId).pipe(
          map((data) => {
            return CourseActionsV2.loadAdminCourseHistorySuccess(data);
          }),
          catchError((error) => {
            const serializedError = {
              status: error?.status,
              statusText: error?.statusText,
              message: error?.message,
              error: error?.error,
            };
            return of(CourseActionsV2.loadAdminCourseHistoryFailed({ error: serializedError }));
          })
        );
      })
    )
  );

  loadAdminCourseCurriculum$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadAdminCourseCurriculum),
      switchMap((payload) => {
        return this.coursesService.getAdminCourseCurriculum(payload.courseId, payload.params).pipe(
          map((data) => {
            this.loaderService.loaderAction('loading-admin-courses-curriculumV2', 'success');
            return CourseActionsV2.loadAdminCourseCurriculumSuccess(data);
          }),
          catchError((error) => {
            this.loaderService.loaderAction('loading-admin-courses-curriculumV2', 'failed');
            const serializedError = {
              status: error?.status,
              statusText: error?.statusText,
              message: error?.message,
              error: error?.error,
            };
            return of(CourseActionsV2.loadAdminCourseCurriculumFail({ error: serializedError }));
          })
        );
      })
    )
  );

  loadAdminCourseDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadAdminCourseDetails),
      switchMap((params) => {
        return this.coursesService.getAdminCourseDetails(params.courseId).pipe(
          map((data) => {
            this.loaderService.loaderAction('loading-admin-courses-detailsV2', 'success');
            return CourseActionsV2.loadAdminCourseDetailsSuccess(data);
          }),
          catchError((error) => {
            this.loaderService.loaderAction('loading-admin-courses-detailsV2', 'failed');
            const serializedError = {
              status: error?.status,
              statusText: error?.statusText,
              message: error?.message,
              error: error?.error,
            };
            return of(CourseActionsV2.loadAdminCourseDetailsFail({ error: serializedError }));
          })
        );
      })
    )
  );

  loadAdminCourseSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadAdminCourseDetailsSuccess),
      mergeMap((courseDetail) => {
        return of(
          CourseActionsV2.loadAdminCourseHistory({ courseId: courseDetail.project }),
          // CourseActionsV2.loadAnnotationsV2({ projectId: courseDetail.project })
          CourseActions.loadHistory({ projectId: courseDetail.project })
        );
      })
    )
  );

  loadAdminCourseLearner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadAdminCourseLearner),
      switchMap((params) => {
        return this.coursesService.getAdminCourseIntend(params.courseId).pipe(
          map((data) => {
            this.loaderService.loaderAction('loading-admin-courses-learnerV2', 'success');
            return CourseActionsV2.loadAdminCourseLearnerSuccess(data);
          }),
          catchError((error) => {
            this.loaderService.loaderAction('loading-admin-courses-learnerV2', 'failed');
            const serializedError = {
              status: error?.status,
              statusText: error?.statusText,
              message: error?.message,
              error: error?.error,
            };
            return of(CourseActionsV2.loadAdminCourseLearnerFail({ error: serializedError }));
          })
        );
      })
    )
  );

  //livesession
  //load courses (initial load - replaces cache)
  loadCourses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadCourses),
      switchMap((payload) => {
        setTimeout(() => {
          this.loaderService.loaderAction('loading-live-session-courses', 'loading');
        });
        return this.liveSessionService.getCourses(payload.params).pipe(
          map((courses) => {
            this.loaderService.loaderAction('loading-live-session-courses', 'success');
            this.paginationService.getPager(courses.count, payload.params.page, payload.params.page_size);
            return CourseActionsV2.loadCoursesSuccess({
              courses: courses.results,
              count: courses.count,
              page: payload.params.page ?? 1,
            });
          }),
          catchError(({ error }) => {
            error?.errors?.map((error: { detail: string }) =>
              this.toastService.error({
                message: error.detail,
              })
            );
            this.loaderService.loaderAction('loading-live-session-courses', 'failed');
            return of(CourseActionsV2.loadCoursesFailed(error));
          })
        );
      })
    )
  );

  // Load more courses (append to cache)
  loadMoreCourses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadMoreCourses),
      switchMap((payload) => {
        setTimeout(() => {
          this.loaderService.loaderAction('loading-more-live-session-courses', 'loading');
        });
        return this.liveSessionService.getCourses(payload.params).pipe(
          map((courses) => {
            this.loaderService.loaderAction('loading-more-live-session-courses', 'success');
            return CourseActionsV2.loadMoreCoursesSuccess({
              courses: courses.results,
              count: courses.count,
              page: payload.params.page ?? 1,
            });
          }),
          catchError(({ error }) => {
            this.loaderService.loaderAction('loading-more-live-session-courses', 'failed');
            return of(CourseActionsV2.loadCoursesFailed(error));
          })
        );
      })
    )
  );

  //load courses
  loadCourse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadCourse),
      switchMap(({ projectId, params }) => {
        setTimeout(() => {
          this.loaderService.loaderAction('loading-live-session-course', 'loading');
        });
        return this.liveSessionService.getCourse(projectId, params).pipe(
          map((course) => {
            this.loaderService.loaderAction('loading-live-session-course', 'success');
            return CourseActionsV2.loadCourseSuccess({ course: course.data });
          }),
          catchError(({ error }) => {
            error.errors.map((error: { detail: string }) =>
              this.toastService.error({
                message: error.detail,
              })
            );
            this.loaderService.loaderAction('loading-live-session-courses', 'failed');
            return of(CourseActionsV2.loadCoursesFailed(error));
          })
        );
      })
    )
  );
  // CLASS SETUP
  loadClassSetup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.loadClassSetup),
      switchMap(({ projectId, params }) =>
        this.liveSessionService
          .getLiveSessionClassSetup(projectId, params)
          .pipe(map((data) => CourseActionsV2.loadClassSetupSuccess({ data })))
      )
    )
  );

  updateClassSetup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CourseActionsV2.updateClassSetup),
      switchMap(({ projectId, payload }) => {
        this.loaderService.loaderAction('prevent-button-action-based-on-api-response', 'loading');
        return this.liveSessionService.updateEventType(projectId, payload).pipe(
          map((data) => {
            this.loaderService.loaderAction('prevent-button-action-based-on-api-response', 'success');
            this.loaderService.loaderAction('course-save-action', 'success');
            this.loaderService.loaderAction('course-pricing-header-button-action', 'success');
            return CourseActionsV2.upsertClassSetupSuccess({ data });
          }),
          catchError((error) => {
            this.loaderService.loaderAction('prevent-button-action-based-on-api-response', 'failed');
            this.loaderService.loaderAction('course-save-action', 'failed');
            this.loaderService.loaderAction('course-pricing-header-button-action', 'failed');
            // Extract a serializable payload — the raw HttpErrorResponse has
            // class-instance methods that fail NgRx's strictActionSerializability
            // check. `error.error` is USUALLY the parsed JSON body (plain
            // object) but for network failures can be a ProgressEvent /
            // ErrorEvent which carries non-serializable fields. Round-trip
            // through JSON.parse(JSON.stringify(...)) to drop functions,
            // symbols, and circular refs; fall back to the raw value on
            // primitives (strings/numbers pass through untouched).
            return of(
              CourseActionsV2.upsertClassSetupError({
                error: {
                  status: error?.status ?? null,
                  body: safeSerializeBody(error?.error),
                },
              })
            );
          })
        );
      })
    )
  );

  // // UPSERT Live session course details
  // upsertLiveSessionCourse$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(CourseActionsV2.upsertLiveSessionCourse),
  //     switchMap(({ courseDetails, courseId }) => {
  //       this.loaderService.loaderAction('prevent-button-action-based-on-api-response', 'loading');
  //       this.loaderService.loaderAction('live-course-save-action', 'loading');
  //       return this.liveSessionService.updateLiveSessionCourse(courseId, courseDetails).pipe(
  //         map((res) => {
  //           this.loaderService.loaderAction('prevent-button-action-based-on-api-response', 'success');
  //           this.loaderService.loaderAction('live-course-save-action', 'success');
  //           return CourseActionsV2.upsertLiveSessionCourseSuccess({ courseDetails: res });
  //         }),
  //         catchError((error) => {
  //           this.loaderService.loaderAction('live-course-save-action', 'failed');
  //           this.loaderService.loaderAction('prevent-button-action-based-on-api-response', 'failed');
  //           return of(CourseActionsV2.upsertLiveSessionCourseError({ error }));
  //         })
  //       );
  //     })
  //   )
  // );

}
